import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { mapSupabaseError, getUserFriendlyMessage } from '../lib/errors';
import { captureError } from '../lib/sentry';
import { useToast } from '../components/Toast';

/**
 * ჩამომტანი კომპანიების მართვის hooks
 */

// ყველა ჩამომტანი კომპანიის მიღება
export function useShippingCompanies() {
  const toast = useToast();

  return useQuery({
    queryKey: ['shipping-companies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shipping_companies')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, {
          tags: { feature: 'shipping_companies', action: 'fetch' }
        });
        throw apiError;
      }
      return data;
    },
    staleTime: 1000 * 60 * 10, // 10 წუთი
    gcTime: 1000 * 60 * 60, // 1 საათი
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}

// ჩამომტანი კომპანიის დამატება
export function useAddShippingCompany() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (company) => {
      const { data, error } = await supabase
        .from('shipping_companies')
        .insert([{ ...company, is_active: true }])
        .select()
        .single();

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, {
          tags: { feature: 'shipping_companies', action: 'insert' }
        });
        throw apiError;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-companies'] });
      toast.success('კომპანია დამატებულია!');
    },
    onError: (error) => {
      toast.error(getUserFriendlyMessage(error));
    },
  });
}

// ჩამომტანი კომპანიის განახლება
export function useUpdateShippingCompany() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, ...company }) => {
      const { data, error } = await supabase
        .from('shipping_companies')
        .update(company)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, {
          tags: { feature: 'shipping_companies', action: 'update' }
        });
        throw apiError;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-companies'] });
      toast.success('კომპანია განახლებულია!');
    },
    onError: (error) => {
      toast.error(getUserFriendlyMessage(error));
    },
  });
}

// ჩამომტანი კომპანიის წაშლა
export function useDeleteShippingCompany() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('shipping_companies')
        .update({ is_active: false })
        .eq('id', id);

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, {
          tags: { feature: 'shipping_companies', action: 'delete' }
        });
        throw apiError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-companies'] });
      toast.success('კომპანია წაიშალა!');
    },
    onError: (error) => {
      toast.error(getUserFriendlyMessage(error));
    },
  });
}

// ============================================
// Source Companies Hooks
// ============================================

// ყველა წყარო კომპანიის მიღება
export function useSourceCompanies() {
  const toast = useToast();

  return useQuery({
    queryKey: ['source-companies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('source_companies')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, {
          tags: { feature: 'source_companies', action: 'fetch' }
        });
        throw apiError;
      }
      return data;
    },
    staleTime: 1000 * 60 * 10, // 10 წუთი
    gcTime: 1000 * 60 * 60, // 1 საათი
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}

// წყარო კომპანიის დამატება
export function useAddSourceCompany() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (company) => {
      const { data, error } = await supabase
        .from('source_companies')
        .insert([{ ...company, is_active: true }])
        .select()
        .single();

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, {
          tags: { feature: 'source_companies', action: 'insert' }
        });
        throw apiError;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['source-companies'] });
      toast.success('კომპანია დამატებულია!');
    },
    onError: (error) => {
      toast.error(getUserFriendlyMessage(error));
    },
  });
}

// წყარო კომპანიის განახლება
export function useUpdateSourceCompany() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, ...company }) => {
      const { data, error } = await supabase
        .from('source_companies')
        .update(company)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, {
          tags: { feature: 'source_companies', action: 'update' }
        });
        throw apiError;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['source-companies'] });
      toast.success('კომპანია განახლებულია!');
    },
    onError: (error) => {
      toast.error(getUserFriendlyMessage(error));
    },
  });
}

// წყარო კომპანიის წაშლა
export function useDeleteSourceCompany() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('source_companies')
        .update({ is_active: false })
        .eq('id', id);

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, {
          tags: { feature: 'source_companies', action: 'delete' }
        });
        throw apiError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['source-companies'] });
      toast.success('კომპანია წაიშალა!');
    },
    onError: (error) => {
      toast.error(getUserFriendlyMessage(error));
    },
  });
}

// ============================================
// Currency Rates Hooks
// ============================================

// ყველა ვალუტის კურსის მიღება
export function useCurrencyRates() {
  const toast = useToast();

  return useQuery({
    queryKey: ['currency-rates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('currency_rates')
        .select('*')
        .eq('is_active', true);

      if (error) {
        const apiError = mapSupabaseError(error);
        captureError(apiError, {
          tags: { feature: 'currency_rates', action: 'fetch' }
        });
        throw apiError;
      }
      return data;
    },
    staleTime: 1000 * 60 * 30, // 30 წუთი
    gcTime: 1000 * 60 * 60, // 1 საათი
    onError: (error) => {
      const apiError = mapSupabaseError(error);
      toast.error(getUserFriendlyMessage(apiError));
    },
  });
}

// ვალუტის კურსის მიღება კოდის მიხედვით
export function useCurrencyRate(currencyCode = 'USD') {
  const { data: rates, isLoading, error } = useCurrencyRates();

  const rate = rates?.find(r => r.currency_code === currencyCode);

  return {
    rate: rate?.rate || 2.75,
    isLoading,
    error,
  };
}
